import { useMemo } from 'react';
import {
  Frame,
  Grid,
  SageClassnames,
  useSageBreakpoint,
  Card,
} from '@kajabi/sage-react';

interface TransformedUnsplashObject {
  image_url: string;
  alt_description?: string;
}

interface PropsType {
  userData: any;
  previewImageUrls: TransformedUnsplashObject[];
  heroImageUrl: string;
}

const LandingPagePreview = ({
  userData,
  previewImageUrls,
  heroImageUrl,
}: PropsType) => {
  const breakpoints = useSageBreakpoint();

  const landingPageData = useMemo(() => {
    return userData.ai_data.landing_pages;
  }, [userData]);

  return (
    <Frame
      style={{
        width: breakpoints.lg ? '100%' : '70%',
        border: breakpoints.sm ? '1rem solid #eceeef' : null,
        backgroundColor: breakpoints.sm ? '#eceeef' : null,
        borderRadius: breakpoints.sm ? '32px' : '15px',
        boxShadow: breakpoints.sm ? 'rgba(0, 0, 0, 0.1) 0px 20px 40px' : null,
      }}
      align={Frame.ALIGNMENTS.STRETCH_CENTER}
    >
      <Frame
        gap={Frame.GAPS.NONE}
        className={`container ${
          SageClassnames.SPACERS[breakpoints.md ? 'NONE' : 'LG_TOP']
        }`}
        style={{
          width: [breakpoints.md ? '100%' : '732px'],
          boxShadow: breakpoints.sm ? null : 'rgba(0, 0, 0, 0.1) 0px 20px 40px',
          borderRadius: breakpoints.sm ? '24px' : '15px',
          height: breakpoints.md ? '576px' : '720px',
          overflowY: 'scroll',
        }}
      >
        {/* Mock window component with 3 buttons */}
        {breakpoints.sm || (
          <Frame
            background="#ECEEEF"
            style={{
              height: '42px',
              borderTopLeftRadius: '15px',
              borderTopRightRadius: '15px',
            }}
          >
            <Frame
              direction={Frame.DIRECTIONS.HORIZONTAL}
              padding={Frame.PADDINGS.SM}
            >
              <div
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: '#B5BAC0',
                  borderRadius: '50%',
                }}
              ></div>
              <div
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: '#B5BAC0',
                  borderRadius: '50%',
                  marginLeft: '-15px',
                }}
              ></div>
              <div
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: '#B5BAC0',
                  borderRadius: '50%',
                  marginLeft: '-15px',
                }}
              ></div>
            </Frame>
          </Frame>
        )}

        {/* Landing page navigation component */}
        <Frame
          background="#FFFFFF"
          style={{ height: '61px' }}
          direction={Frame.DIRECTIONS.HORIZONTAL}
          padding={Frame.PADDINGS.SM}
        >
          <Frame
            padding={Frame.PADDINGS.XS}
            direction={Frame.DIRECTIONS.HORIZONTAL}
          >
            <div
              style={{
                width: 87,
                height: breakpoints.sm ? 8 : 12,
                backgroundColor: '#D3D5D9',
                borderRadius: '100px',
              }}
            ></div>
          </Frame>
          {breakpoints.sm ? (
            <Frame
              padding={Frame.PADDINGS.NONE}
              align={Frame.ALIGNMENTS.TOP_RIGHT}
              gap={Frame.GAPS.XXS}
            >
              <div
                style={{
                  width: 32,
                  height: 4,
                  backgroundColor: '#D3D5D9',
                  borderRadius: '100px',
                }}
              />
              <div
                style={{
                  width: 32,
                  height: 4,
                  backgroundColor: '#D3D5D9',
                  borderRadius: '100px',
                }}
              />
              <div
                style={{
                  width: 32,
                  height: 4,
                  backgroundColor: '#D3D5D9',
                  borderRadius: '100px',
                }}
              />
            </Frame>
          ) : (
            <Frame
              padding={Frame.PADDINGS.XS}
              align={Frame.ALIGNMENTS.TOP_RIGHT}
              direction={Frame.DIRECTIONS.HORIZONTAL}
            >
              <div
                style={{
                  width: 48,
                  height: 8,
                  backgroundColor: '#E9EBED',
                  borderRadius: '100px',
                }}
              ></div>
              <div
                style={{
                  width: 48,
                  height: 8,
                  backgroundColor: '#E9EBED',
                  borderRadius: '100px',
                }}
              ></div>
              <div
                style={{
                  width: 48,
                  height: 8,
                  backgroundColor: '#E9EBED',
                  borderRadius: '100px',
                }}
              ></div>
              <div
                style={{
                  width: 48,
                  height: 8,
                  backgroundColor: '#E9EBED',
                  borderRadius: '100px',
                }}
              ></div>
              <div
                style={{
                  width: 48,
                  height: 8,
                  backgroundColor: '#E9EBED',
                  borderRadius: '100px',
                }}
              ></div>
            </Frame>
          )}
        </Frame>

        {/* Landing page hero image component */}
        <Frame
          style={{
            minHeight: breakpoints.sm ? '389px' : '347px',
            backgroundImage: `
                        linear-gradient(rgba(0,0,0,.65), rgba(0,0,0,.65)),
                        url(${heroImageUrl})
                    `,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
          }}
          padding={Frame.PADDINGS.LG}
          gap={Frame.GAPS.XS}
          align={Frame.ALIGNMENTS.CENTER_CENTER}
        >
          <Grid.Row>
            <Grid.Col>
              <Frame
                style={{
                  width: breakpoints.sm ? null : '319px',
                  height: breakpoints.sm ? null : '164px',
                  borderRadius: '4px',
                  justifyContent: 'center',
                  textAlign: breakpoints.sm ? 'center' : null,
                }}
                gap={Frame.GAPS.XXS}
                className={SageClassnames.SPACERS.SM_BOTTOM}
              >
                <h3
                  style={{
                    fontWeight: 700,
                  }}
                  className={`${SageClassnames.TYPE.HEADING_3} ${SageClassnames.TYPE_COLORS.WHITE}`}
                >
                  {landingPageData.title}
                </h3>
                <p
                  style={{
                    fontSize: breakpoints.md ? 14 : 12,
                    fontWeight: 500,
                    lineHeight: '16px',
                  }}
                  className={`${SageClassnames.TYPE.BODY_XSMALL_MED} ${SageClassnames.TYPE_COLORS.WHITE}`}
                >
                  {landingPageData.description}
                </p>
              </Frame>
            </Grid.Col>
            <Grid.Col>
              <Frame
                style={{
                  backgroundColor: 'white',
                  width: breakpoints.sm ? '278px' : '319px',
                  height: '206px',
                  borderRadius: '4px',
                  justifyContent: 'center',
                }}
                gap={Frame.GAPS.SM}
              >
                <Frame
                  style={{
                    alignItems: 'center',
                  }}
                  gap={Frame.GAPS.XS}
                >
                  <p className={SageClassnames.TYPE.HEADING_6}>
                    Join our program
                  </p>
                  <Card
                    style={{
                      maxHeight: '32px',
                      width: '237px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '1rem',
                      borderRadius: '10px',
                    }}
                  >
                    <p
                      className={`${SageClassnames.TYPE_COLORS.CHARCOAL_200} ${SageClassnames.TYPE.BODY_XSMALL}`}
                    >
                      Name
                    </p>
                  </Card>
                  <Card
                    style={{
                      maxHeight: '32px',
                      width: '237px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '1rem',
                      borderRadius: '10px',
                    }}
                  >
                    <p
                      className={`${SageClassnames.TYPE_COLORS.CHARCOAL_200} ${SageClassnames.TYPE.BODY_XSMALL}`}
                    >
                      Email
                    </p>
                  </Card>
                  <div
                    style={{
                      width: 100,
                      height: 28,
                      backgroundColor: '#0072EF',
                      borderRadius: '999px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    className={SageClassnames.SPACERS.XS_TOP}
                  >
                    <p
                      style={{
                        fontSize: '12px',
                        color: 'white',
                        margin: 'auto',
                      }}
                    >
                      Signup now
                    </p>
                  </div>
                </Frame>
              </Frame>
            </Grid.Col>
          </Grid.Row>
        </Frame>

        {/* Landing page courses preview component */}
        <Frame
          style={{
            width: breakpoints.sm ? null : '574px',
            height: breakpoints.sm ? null : '185px',
            margin: 'auto',
            borderRadius: '4px',
            justifyContent: 'center',
            textAlign: 'center',
            backgroundColor: 'white',
          }}
          gap={Frame.GAPS.LG}
          padding={Frame.PADDINGS[breakpoints.sm ? 'MD' : 'XS']}
          direction={
            Frame.DIRECTIONS[breakpoints.sm ? 'VERTICAL' : 'HORIZONTAL']
          }
        >
          {landingPageData.value_propositions.length > 0 &&
            landingPageData.value_propositions.map((lesson: any, i: number) => (
              <Frame
                key={`landing-page-display-cards-${i}`}
                gap={Frame.GAPS.XS}
                direction={Frame.DIRECTIONS.VERTICAL}
              >
                <Frame
                  className="preview-image-landing-page"
                  style={{
                    backgroundImage: `url(${previewImageUrls[i]?.image_url})`,
                    width: breakpoints.sm ? '200px' : '175px',
                    height: breakpoints.sm ? '100px' : '103px',
                    alignSelf: 'center',
                  }}
                />
                <p
                  className={SageClassnames.TYPE.BODY_SMALL_MED}
                  style={{
                    fontWeight: '550',
                    alignSelf: 'center',
                  }}
                >
                  {lesson.title}
                </p>
                <p
                  className={SageClassnames.TYPE.BODY_XSMALL}
                  style={{
                    alignSelf: 'center',
                  }}
                >
                  {lesson.description}
                </p>
              </Frame>
            ))}
        </Frame>
      </Frame>
    </Frame>
  );
};

export default LandingPagePreview;
